import React from "react";
import cs from "classnames";
import styles from "./styles.module.scss";
import { AccordionChevronIcon } from "../NewOrderDailyLimits/icons";
import { NewOrderAppOpeningsCardProps } from "./types";

const CheckIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path
      d="M3.5 8.2 6.6 11.3 12.5 5.4"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const StepToggle: React.FC<{
  onTitleToggle?: () => void;
  collapsed?: boolean;
}> = ({ onTitleToggle, collapsed }) =>
  onTitleToggle ? (
    <button
      type="button"
      className={cs(styles.stepToggle, styles.stepToggleButton, {
        [styles.stepToggleCollapsed]: collapsed,
      })}
      onClick={onTitleToggle}
      aria-label={collapsed ? "Expand step" : "Collapse step"}
    >
      <AccordionChevronIcon className={styles.accordionChevron} />
    </button>
  ) : (
    <span
      className={cs(styles.stepToggle, {
        [styles.stepToggleCollapsed]: collapsed,
      })}
      aria-hidden
    >
      <AccordionChevronIcon className={styles.accordionChevron} />
    </span>
  );

/**
 * "3. App openings" step card of the new-order flow (Figma 560:104035):
 * one line — chevron + title, Day 1 / Day 2 checkboxes (Day 1 is always on)
 * and the step price at the right. Collapsed swaps the checkboxes for a
 * "Day 1, 2" summary. Pure presentational — days and handlers come from props.
 */
export const NewOrderAppOpeningsCard: React.FC<NewOrderAppOpeningsCardProps> = ({
  collapsed = false,
  stepTitle = "3. App openings",
  onTitleToggle,
  days,
  onDayToggle,
  price,
  formula,
  doneDisabled = false,
  onDone,
  className,
}) => {
  const checkedNumbers = days
    .filter((day) => day.checked)
    .map((day) => day.label.replace(/\D/g, ""))
    .join(", ");

  return (
    <div
      className={cs(
        styles.card,
        { [styles.cardCollapsed]: collapsed },
        className,
      )}
    >
      <div className={styles.titleCol}>
        <StepToggle onTitleToggle={onTitleToggle} collapsed={collapsed} />
        <h3 className={styles.stepTitle}>{stepTitle}</h3>
      </div>

      {collapsed ? (
        <div className={styles.summary}>
          <span className={styles.summaryCaption}>Day</span>
          <span className={styles.summaryValue}>{checkedNumbers}</span>
        </div>
      ) : (
        <div className={styles.daysGroup}>
          {days.map((day) => (
            <label
              key={day.label}
              className={cs(styles.dayItem, {
                [styles.dayItemDisabled]: day.disabled,
              })}
            >
              <input
                type="checkbox"
                className={styles.dayInput}
                checked={day.checked}
                disabled={day.disabled}
                onChange={() => onDayToggle?.(day.label)}
              />
              <span
                className={cs(styles.dayBox, {
                  [styles.dayBoxChecked]: day.checked,
                  [styles.dayBoxLocked]: day.checked && day.disabled,
                })}
                aria-hidden
              >
                {day.checked && <CheckIcon className={styles.dayCheck} />}
              </span>
              {day.label}
            </label>
          ))}
        </div>
      )}

      {collapsed ? (
        <span className={styles.price}>{price}</span>
      ) : (
        <>
          {formula && <span className={styles.formula}>{formula}</span>}
          <span className={styles.priceExpanded}>{price}</span>
          <div className={styles.doneWrap}>
            <button
              type="button"
              className={styles.doneButton}
              disabled={doneDisabled}
              onClick={onDone}
            >
              Done
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default NewOrderAppOpeningsCard;
